(function () {
  if (window.FabCpLibrary) return;

  const LS_KEY = 'fab_cp_photo_library_v1';
  const DB_NAME = 'fab_custom_print';
  const DB_VERSION = 1;
  const STORE = 'previews';
  const MAX_ITEMS = 12;
  // CP-6: eviction deletes library preview blobs (key = library item id) only.
  // cart:{uploadId} blobs are separate keys and are NOT purged by the 12-item cap.
  // Cross-device cart thumbnails always rely on line item _preview_url.

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'fab-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
  }

  function readMeta() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return { activeId: null, items: [] };
      const parsed = JSON.parse(raw);
      return {
        activeId: parsed.activeId || null,
        items: Array.isArray(parsed.items) ? parsed.items : [],
      };
    } catch (_) {
      return { activeId: null, items: [] };
    }
  }

  function writeMeta(meta) {
    localStorage.setItem(LS_KEY, JSON.stringify({
      activeId: meta.activeId || null,
      items: meta.items || [],
    }));
  }

  function openDb() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onerror = () => reject(req.error || new Error('idb_open_failed'));
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
    });
  }

  async function idbPut(id, blob) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.objectStore(STORE).put(blob, id);
    });
  }

  async function idbGet(id) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      tx.onerror = () => { db.close(); reject(tx.error); };
      const req = tx.objectStore(STORE).get(id);
      req.onsuccess = () => { db.close(); resolve(req.result || null); };
      req.onerror = () => { db.close(); reject(req.error); };
    });
  }

  async function idbDelete(id) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.objectStore(STORE).delete(id);
    });
  }

  async function makeThumbDataUrl(blob, maxW) {
    maxW = maxW || 120;
    if (!blob) return '';
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = url;
      });
      const w = Math.min(maxW, img.naturalWidth || maxW);
      const h = Math.round((img.naturalHeight || maxW) * (w / (img.naturalWidth || w)));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', 0.8);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function FabCpLibrary() {
    this._meta = readMeta();
  }

  FabCpLibrary.prototype.reload = function () {
    this._meta = readMeta();
    return this._meta;
  };

  FabCpLibrary.prototype.list = function () {
    return this._meta.items.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  };

  FabCpLibrary.prototype.getActiveId = function () {
    return this._meta.activeId;
  };

  FabCpLibrary.prototype.setActiveId = function (id) {
    this._meta.activeId = id || null;
    writeMeta(this._meta);
  };

  FabCpLibrary.prototype.getMeta = function (id) {
    return this._meta.items.find((item) => item.id === id) || null;
  };

  FabCpLibrary.prototype.getPreviewBlob = function (id) {
    return idbGet(id);
  };

  FabCpLibrary.prototype._cartPreviewKey = function (uploadId) {
    return uploadId ? 'cart:' + uploadId : '';
  };

  FabCpLibrary.prototype.saveCartPreview = async function (uploadId, blob) {
    const key = this._cartPreviewKey(uploadId);
    if (!key || !blob) return;
    await idbPut(key, blob);
  };

  FabCpLibrary.prototype.getCartPreviewBlob = function (uploadId) {
    const key = this._cartPreviewKey(uploadId);
    return key ? idbGet(key) : Promise.resolve(null);
  };

  FabCpLibrary.prototype.save = async function (input) {
    const now = Date.now();
    let id = input.id || uuid();
    const existing = this._meta.items.find((item) => item.id === id);
    const thumbDataUrl = input.thumbDataUrl || await makeThumbDataUrl(input.previewBlob);

    if (input.previewBlob) {
      await idbPut(id, input.previewBlob);
    }

    const record = {
      id,
      name: input.name || 'Your photo',
      orientation: input.orientation || 'portrait',
      cropBox: input.cropBox || null,
      uploadId: input.uploadId || null,
      previewUrl: input.previewUrl || null,
      thumbDataUrl,
      createdAt: existing ? existing.createdAt : now,
      updatedAt: now,
    };

    let items = this._meta.items.filter((item) => item.id !== id);
    items.unshift(record);

    if (items.length > MAX_ITEMS) {
      const removed = items.slice(MAX_ITEMS);
      items = items.slice(0, MAX_ITEMS);
      for (const r of removed) {
        if (r.id !== id) await idbDelete(r.id).catch(() => {});
      }
    }

    this._meta.items = items;
    this._meta.activeId = id;
    writeMeta(this._meta);
    return record;
  };

  FabCpLibrary.prototype.remove = async function (id) {
    await idbDelete(id).catch(() => {});
    this._meta.items = this._meta.items.filter((item) => item.id !== id);
    if (this._meta.activeId === id) {
      this._meta.activeId = this._meta.items[0] ? this._meta.items[0].id : null;
    }
    writeMeta(this._meta);
    return this._meta.activeId;
  };

  FabCpLibrary.prototype.updateMeta = async function (id, patch) {
    const item = this._meta.items.find((i) => i.id === id);
    if (!item) return null;
    Object.assign(item, patch, { updatedAt: Date.now() });
    writeMeta(this._meta);
    return item;
  };

  FabCpLibrary.makeThumbDataUrl = makeThumbDataUrl;
  window.FabCpLibrary = FabCpLibrary;
})();
